import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { InfoHint } from "../components/InfoHint";
import { InstallPrompt } from "../components/InstallPrompt";
import { BookOpen, Download, PlayCircle, CheckCircle2, FileText, Search, Users, Building2, Wrench, Scale, ShoppingBag, Pencil } from "lucide-react";
import { GuideEditorModal } from "../components/GuideEditorModal";

// Iter121m · Mappa ruolo dell'utente → chiave destinatario nel catalogo statico delle guide.
// Serve a restringere automaticamente la libreria alle sole guide di competenza per i non-admin.
const RUOLO_TO_DESTINATARIO = {
  studio: "studio_admin",
  studio_prova: "studio_admin",
  condomino: "condomino",
  fornitore: "fornitore",
  avvocato: "avvocato",
  studio_cliente: "studio_cliente",
  cliente_credius: "cliente_credius",
};

// Metadata dei destinatari (chiavi allineate col catalogo generato da generate_all_guides.py)
const DESTINATARI = [
  { key: "studio_admin",   label: "Studio · Admin & Super Admin", icon: Building2, color: "bg-brand/10 text-brand border-brand/30" },
  { key: "fornitore",      label: "Fornitore",                    icon: Wrench,    color: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "condomino",      label: "Condòmino",                    icon: Users,     color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "studio_cliente", label: "Studio · Cliente / Overview",  icon: ShoppingBag, color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  { key: "avvocato",       label: "Avvocato · Credius",           icon: Scale,     color: "bg-purple-100 text-purple-800 border-purple-300" },
  { key: "cliente_credius",label: "Cliente Credius",              icon: Scale,     color: "bg-sky-100 text-sky-800 border-sky-300" },
];

export default function Guida() {
  const navigate = useNavigate();
  const [manuale, setManuale] = useState(null);
  const [scaricando, setScaricando] = useState(false);
  const [catalogo, setCatalogo] = useState(null);
  const [filtroRuolo, setFiltroRuolo] = useState("all");
  const [cerca, setCerca] = useState("");
  // Iter121m · ruolo dell'utente + ambito allowed (calcolato al fetch di /manuali/mio)
  const [me, setMe] = useState(null); // {role, account_type}
  // Iter122k137 · super_admin (Studio Izzo) può modificare il testo dei PDF
  // Iter122k143 · anche l'Admin Izzo (role=admin + tenant studio) è autorizzato.
  const [guideEdit, setGuideEdit] = useState(null); // gid to edit
  const [overrides, setOverrides] = useState({}); // { gid: has_override }

  useEffect(() => {
    Promise.all([
      api.get("/manuali/mio").then((r) => r.data).catch(() => null),
      api.get("/auth/me").then((r) => r.data).catch(() => null),
    ]).then(([manualeData, meData]) => {
      if (manualeData) setManuale(manualeData);
      if (meData) setMe(meData);
      // Se super_admin o Admin Izzo (tenant studio), carica lo stato override per guida
      const canEdit = meData?.role === "super_admin"
        || (meData?.role === "admin" && meData?.tenant_id === "studio");
      if (canEdit) {
        api.get("/guide/editabili").then((r) => {
          const map = {};
          (r.data?.guide || []).forEach((g) => { map[g.id] = g.has_override; });
          setOverrides(map);
        }).catch(() => {});
      }
    });
    fetch("/guide/catalogo.json").then((r) => r.ok ? r.json() : null)
      .then((j) => setCatalogo(j)).catch(() => {});
  }, []);

  const isSuperAdmin = me?.role === "super_admin"
    || (me?.role === "admin" && me?.tenant_id === "studio");
  const refreshOverrides = () => {
    if (!isSuperAdmin) return;
    api.get("/guide/editabili").then((r) => {
      const map = {};
      (r.data?.guide || []).forEach((g) => { map[g.id] = g.has_override; });
      setOverrides(map);
    }).catch(() => {});
  };

  // Iter121m · Restringe il ruolo se non è admin (Studio): un condòmino vede solo le guide condomino, ecc.
  const isAdmin = me?.role === "admin";
  const restrictedDestinatario = isAdmin ? null : RUOLO_TO_DESTINATARIO[manuale?.ruolo] || null;

  const scaricaFile = (url, filename) => {
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const scaricaPdf = async () => {
    setScaricando(true);
    try {
      const res = await api.get("/manuali/mio/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `manuale_${manuale?.ruolo || "ginius"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
    setScaricando(false);
  };

  const scaricaScheda = async () => {
    try {
      const res = await api.get("/manuali/scheda/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "scheda_informativa_app.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const guideFiltrate = useMemo(() => {
    if (!catalogo?.guide) return [];
    const q = (cerca || "").trim().toLowerCase();
    return catalogo.guide.filter((g) => {
      // Iter121m · restrizione hard: i non-admin vedono SOLO il proprio destinatario
      if (restrictedDestinatario && g.destinatario !== restrictedDestinatario) return false;
      if (filtroRuolo !== "all" && g.destinatario !== filtroRuolo) return false;
      if (!q) return true;
      const hay = [g.titolo, g.sottotitolo, g.descrizione, ...(g.capitoli || [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [catalogo, filtroRuolo, cerca, restrictedDestinatario]);

  const contatoriPerRuolo = useMemo(() => {
    const m = {};
    (catalogo?.guide || []).forEach((g) => {
      // Iter121m · il conteggio riflette la restrizione (se attiva)
      if (restrictedDestinatario && g.destinatario !== restrictedDestinatario) return;
      m[g.destinatario] = (m[g.destinatario] || 0) + 1;
    });
    return m;
  }, [catalogo, restrictedDestinatario]);

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 mb-2">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
          <BookOpen className="w-9 h-9 text-brand" /> Guide e manuali
        </h1>
        <InfoHint section="guida" />
      </div>
      <p className="text-base text-slate-500 mb-4">Libreria dei manuali illustrati, presentazione guidata e installazione dell'app.</p>

      {/* Libreria guide per ruolo */}
      <Card className="p-4 sm:p-5 mb-5 border-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-display text-xl font-bold text-slate-800">
            Libreria guide ({restrictedDestinatario ? (contatoriPerRuolo[restrictedDestinatario] || 0) : (catalogo?.totale_guide ?? "…")})
          </h2>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
              placeholder="Cerca per capitolo o parola…"
              className="pl-9 h-10" data-testid="guida-search" />
          </div>
        </div>

        {/* Iter121m · Tabs ruolo — visibili solo agli admin (Studio); i non-admin vedono già filtrate le proprie */}
        {isAdmin ? (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="guida-filter-ruolo">
            <button onClick={() => setFiltroRuolo("all")}
              className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${filtroRuolo === "all" ? "bg-brand text-white border-brand" : "bg-white text-slate-700 border-slate-200 hover:border-brand"}`}
              data-testid="guida-tab-all">
              Tutti ({catalogo?.totale_guide || 0})
            </button>
            {DESTINATARI.map((d) => {
              const Ic = d.icon; const n = contatoriPerRuolo[d.key] || 0;
              const active = filtroRuolo === d.key;
              return (
                <button key={d.key} onClick={() => setFiltroRuolo(d.key)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold border-2 transition-colors ${active ? "bg-brand text-white border-brand" : `bg-white text-slate-700 border-slate-200 hover:border-brand`}`}
                  data-testid={`guida-tab-${d.key}`}>
                  <Ic className="w-4 h-4" /> {d.label} ({n})
                </button>
              );
            })}
          </div>
        ) : restrictedDestinatario ? (
          <div className="mb-3 text-xs text-slate-500 flex items-center gap-2" data-testid="guida-scope-note">
            <FileText className="w-3.5 h-3.5" />
            Stai visualizzando le guide dedicate al tuo ruolo. Per contenuti aggiuntivi contatta lo Studio.
          </div>
        ) : null}

        {/* Elenco guide filtrate */}
        {!catalogo ? (
          <p className="text-slate-500 py-6">Caricamento libreria…</p>
        ) : guideFiltrate.length === 0 ? (
          <p className="text-slate-500 py-6 italic">Nessuna guida corrisponde ai filtri correnti.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {guideFiltrate.map((g) => {
              const dest = DESTINATARI.find((d) => d.key === g.destinatario);
              const Ic = dest?.icon || FileText;
              return (
                <div key={g.id} className="p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-brand transition-colors"
                     data-testid={`guida-card-${g.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${dest?.color || "bg-slate-100 border-slate-300"}`}>
                        <Ic className="w-3 h-3" /> {dest?.label || g.destinatario}
                      </span>
                      <h3 className="font-display font-bold text-base mt-1.5">{highlightMatch(g.titolo, cerca)}</h3>
                      {g.sottotitolo && <p className="text-xs text-slate-500 mt-0.5">{highlightMatch(g.sottotitolo, cerca)}</p>}
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 shrink-0">{g.pagine} pag.</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-2">{highlightMatch(g.descrizione, cerca)}</p>
                  {isSuperAdmin && overrides[g.id] && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 mb-2"
                         data-testid={`guida-override-badge-${g.id}`}>
                      <Pencil className="w-3 h-3" /> Versione modificata (rigenerata al download)
                    </div>
                  )}
                  {g.capitoli && g.capitoli.length > 0 && (
                    <details className="text-xs text-slate-600 mb-3">
                      <summary className="cursor-pointer font-bold text-slate-700 hover:text-brand" data-testid={`guida-capitoli-${g.id}`}>
                        Indice ({g.capitoli.length} capitoli)
                      </summary>
                      <ol className="list-decimal ml-5 mt-1.5 space-y-0.5">
                        {g.capitoli.map((c, i) => (
                          <li key={i} className={cerca && c.toLowerCase().includes(cerca.toLowerCase()) ? "font-bold text-brand" : ""}>{c}</li>
                        ))}
                      </ol>
                    </details>
                  )}
                  <div className="flex gap-2">
                    <Button data-testid={`guida-download-${g.id}`}
                      onClick={() => {
                        if (isSuperAdmin && overrides[g.id]) {
                          // Scarica il PDF rigenerato con le modifiche
                          api.get(`/guide/editabili/${g.id}/pdf`, { responseType: "blob" }).then((r) => {
                            const url = window.URL.createObjectURL(new Blob([r.data]));
                            const a = document.createElement("a");
                            a.href = url; a.download = `${g.id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            window.URL.revokeObjectURL(url);
                          }).catch(() => toast.error("Download non riuscito"));
                        } else {
                          scaricaFile(g.file, g.file.split("/").pop());
                        }
                      }}
                      className="flex-1 h-10 bg-brand hover:bg-brand-dark">
                      <Download className="w-4 h-4 mr-2" /> Scarica PDF
                    </Button>
                    {isSuperAdmin && (
                      <Button variant="outline" data-testid={`guida-edit-${g.id}`}
                        onClick={() => setGuideEdit(g.id)}
                        title="Modifica testo (Admin Izzo)"
                        className="h-10 border-brand text-brand hover:bg-brand/10">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <button data-testid="guida-presentazione" onClick={() => navigate("/presentazione")}
          className="text-left p-5 rounded-xl border-2 border-slate-200 hover:border-brand transition-colors bg-white">
          <PlayCircle className="w-8 h-8 text-brand mb-2" />
          <div className="font-bold text-lg">Presentazione guidata</div>
          <div className="text-sm text-slate-500">Scopri l'app in pochi passi</div>
        </button>
        <button data-testid="guida-scarica-pdf" onClick={scaricaPdf} disabled={scaricando}
          className="text-left p-5 rounded-xl border-2 border-slate-200 hover:border-brand transition-colors bg-white disabled:opacity-60">
          <Download className="w-8 h-8 text-brand mb-2" />
          <div className="font-bold text-lg">{scaricando ? "Preparo il PDF…" : "Scarica il manuale (PDF)"}</div>
          <div className="text-sm text-slate-500">Versione stampabile</div>
        </button>
        <button data-testid="guida-scarica-scheda" onClick={scaricaScheda}
          className="text-left p-5 rounded-xl border-2 border-slate-200 hover:border-brand transition-colors bg-white">
          <FileText className="w-8 h-8 text-brand mb-2" />
          <div className="font-bold text-lg">Scheda informativa (PDF)</div>
          <div className="text-sm text-slate-500">Guida rapida allegata all'attivazione</div>
        </button>
        <div className="p-5 rounded-xl border-2 border-slate-200 bg-white" data-testid="guida-install-prompt">
          <FileText className="w-8 h-8 text-brand mb-2" />
          <div className="font-bold text-lg mb-2">Installa l'app</div>
          <InstallPrompt />
          <div className="text-sm text-slate-500 mt-1">Su telefono o desktop</div>
        </div>
      </div>

      {isSuperAdmin ? null : !manuale ? (
        <div className="text-slate-500">Caricamento del manuale…</div>
      ) : (
        <Card className="p-4 border-2" data-testid="manuale-contenuto">
          <h2 className="font-display text-2xl font-bold mb-5 text-brand">{manuale.titolo}</h2>
          <div className="space-y-4">
            {manuale.sezioni.map((s, i) => (
              <div key={i} data-testid={`manuale-sezione-${i}`}>
                <h3 className="font-bold text-lg mb-2">{s.titolo}</h3>
                <ul className="space-y-1.5">
                  {s.punti.map((p, j) => (
                    <li key={j} className="flex gap-2 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      )}

      {guideEdit && (
        <GuideEditorModal gid={guideEdit}
          onClose={() => setGuideEdit(null)}
          onSaved={refreshOverrides} />
      )}
    </div>
  );
}
